import React, { createContext, useContext, useState, useEffect } from 'react';
import { apiClient } from '../services/api';

interface SettingsAccess {
  canRead: boolean;
  canWrite: boolean;
  restrictions?: string[];
}

interface SettingsContextType {
  settingsAccess: Record<string, SettingsAccess>;
  checkAccess: (category: string, type: 'read' | 'write') => boolean;
  getRestrictions: (category: string) => string[];
  refreshAccess: () => Promise<void>;
  loading: boolean;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export const SettingsProvider: React.FC<{ 
  children: React.ReactNode; 
  salonId: string;
}> = ({ children, salonId }) => {
  const [settingsAccess, setSettingsAccess] = useState<Record<string, SettingsAccess>>({});
  const [loading, setLoading] = useState(true);

  const checkAccess = (category: string, type: 'read' | 'write'): boolean => {
    const access = settingsAccess[category];
    return type === 'read' ? access?.canRead || false : access?.canWrite || false;
  };

  const getRestrictions = (category: string): string[] => {
    return settingsAccess[category]?.restrictions || [];
  };

  const refreshAccess = async () => {
    if (!salonId) return;

    const categories = [
      'basic',
      'features',
      'pricing',
      'marketing',
      'security',
      'notifications',
      'analytics',
      'integrations',
      'advanced_reporting',
      'backup',
      'documentation',
      'staff_management',
      'inventory',
      'payment',
      'assistant'
    ];

    try {
      setLoading(true);
      const accessPromises = categories.map(async (category) => {
        try {
          const response = await apiClient.get(`/settings/${salonId}/access/${category}`);
          return { category, access: response.data.access };
        } catch (error) {
          console.warn(`Failed to get access for ${category}:`, error);
          return { category, access: { canRead: false, canWrite: false } };
        }
      });

      const results = await Promise.all(accessPromises);
      const newAccess = results.reduce((acc, { category, access }) => {
        acc[category] = access;
        return acc;
      }, {} as Record<string, SettingsAccess>);

      setSettingsAccess(newAccess);
    } catch (error) {
      console.error('Failed to refresh settings access:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshAccess();
  }, [salonId]);

  return (
    <SettingsContext.Provider value={{
      settingsAccess,
      checkAccess,
      getRestrictions,
      refreshAccess,
      loading
    }}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};